import { ToolbarCompass } from "@circle-react-uikit/TipTapBlockEditor/Toolbar/ToolbarCompass";
import type { EditorApi } from "@circle-react-uikit/TipTapBlockEditor/types";
import { useRef } from "react";

import { t } from "@/i18n-js/instance";
import { useCommentsContainer } from "@/react/components/CommentsCompass/CommentsContainer";
import { Editor } from "@/react/components/CommentsCompass/Editor";
import { useCommentsConfig } from "@/react/components/CommentsCompass/Provider/ConfigContext";
import type { BasicCommunityMember } from "@/react/types/CommunityMember";

import { CompactToolbar } from "./CompactToolbar";
import { usePersistCommentDraft } from "./useCommentDraftStorage";
import { useComposerActivation } from "./useComposerActivation";
import { useComposerCollapse } from "./useComposerCollapse";
import { useComposerMentionPrefill } from "./useComposerMentionPrefill";
import { useComposerReset } from "./useComposerReset";

interface ComposerBodyProps {
  isCollapsible?: boolean;
  isFocusPending: boolean;
  mentionCommunityMember?: BasicCommunityMember | null;
  onFocusHandled: () => void;
  parentCommentId?: number;
  replyToCommunityMember?: BasicCommunityMember | null;
}

export const ComposerBody = ({
  isCollapsible = false,
  mentionCommunityMember,
  parentCommentId,
  replyToCommunityMember,
  isFocusPending,
  onFocusHandled,
}: ComposerBodyProps) => {
  const { spaceId } = useCommentsConfig();
  const { isNarrow } = useCommentsContainer();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const editorApiRef = useRef<EditorApi>(null);

  const { isCollapsed, collapse, onFocus, onBlur } = useComposerCollapse({
    wrapperRef,
    isCollapsible,
  });

  usePersistCommentDraft({ parentCommentId });

  const initialContent = useComposerMentionPrefill(mentionCommunityMember);

  useComposerActivation({
    editorApiRef,
    wrapperRef,
    initialContent,
    isFocusPending,
    onFocusHandled,
  });

  useComposerReset({
    editorApiRef,
    collapse,
    shouldRefocus: !isCollapsible,
  });

  const placeholder = replyToCommunityMember
    ? t("comments.form.fields.body_reply_placeholder", {
        name: replyToCommunityMember.name,
      })
    : t("comments.form.fields.body_placeholder");

  return (
    <div ref={wrapperRef} onFocus={onFocus} onBlur={onBlur}>
      <Editor
        editorApiRef={editorApiRef}
        isCollapsed={isCollapsed}
        placeholder={placeholder}
        spaceId={spaceId}
        toolbar={isNarrow ? CompactToolbar : ToolbarCompass}
      />
    </div>
  );
};
